/**
 * The plan chain with the draft spliced in — "after previous" shows the real
 * start after its campaign group's previous container, not a lone fallback —
 * timed on one run date. Pure, so the General tab's times and "Delete the
 * drops outside the window" (every clock-change day, 19/10/2026) share it.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { ContainerDraft, DraftSlot, DropGenPlanDetail } from '../../../../drop-generator.types';
import {
  computeContainerTimes,
  type ContainerTimes,
  planWindow,
  slotKey,
  type TimedContainerInput,
} from '../../../../utils/slot-times';

/** The container "after the one before" follows: the previous enabled one of its campaign group. */
export interface PreviousContainer {
  name: string;
  color: string;
  /** Its last planned minute — this one's start is that plus the gap. */
  lastMin: number;
}

type TimingField = 'kind' | 'enabled' | 'masterId' | 'startMode' | 'gapMin' | 'startAtMin' | 'endAtMin' | 'intervalMin' | 'sendOutsideWindow';

/** What the timing reads of a draft — nothing else (a name, an amount, a group) moves a drop. */
export type TimingDraft = Pick<ContainerDraft, TimingField> & { slots: Array<Pick<DraftSlot, 'id' | 'enabled' | 'pinnedAtMin' | 'sendOutsideWindow'>> };

export const timingOf = (d: TimingDraft): TimingDraft => ({
  kind: d.kind,
  enabled: d.enabled,
  masterId: d.masterId,
  startMode: d.startMode,
  gapMin: d.gapMin,
  startAtMin: d.startAtMin,
  endAtMin: d.endAtMin,
  intervalMin: d.intervalMin,
  sendOutsideWindow: d.sendOutsideWindow,
  slots: d.slots.map((s) => ({ id: s.id, enabled: s.enabled, pinnedAtMin: s.pinnedAtMin, sendOutsideWindow: s.sendOutsideWindow })),
});

/** The draft as the timing mirror reads it; its drops keyed by slotKey. */
export function draftTimedInput(draft: TimingDraft, containerId: string | null): TimedContainerInput {
  return {
    id: containerId ?? 'draft',
    kind: draft.kind,
    enabled: draft.enabled,
    masterId: draft.masterId,
    startMode: draft.startMode,
    gapMin: draft.gapMin,
    startAtMin: draft.startAtMin,
    endAtMin: draft.endAtMin,
    intervalMin: draft.intervalMin,
    sendOutsideWindow: draft.sendOutsideWindow,
    slots: draft.slots.map((s, i) => ({ id: slotKey(s, i), enabled: s.enabled, pinnedAtMin: s.pinnedAtMin, sendOutsideWindow: s.sendOutsideWindow })),
  };
}

/** The draft's times (and the container it follows) on `runDate` — the board's day when absent. Only the
 *  containers that can move its start are timed (`chainBefore`): the same times as the whole plan's. */
export function chainTimes(
  draft: TimingDraft,
  plan: DropGenPlanDetail,
  containerId: string | null,
  runDate?: string | null,
): { times: ContainerTimes | undefined; previous: PreviousContainer | null } {
  const draftInput = draftTimedInput(draft, containerId);
  const before = chainBefore(plan, containerId, draft.masterId);
  const all = computeContainerTimes([...before, draftInput], planWindow(plan, runDate)); // the board's day, like the planner
  return { times: all.get(draftInput.id), previous: previousOf(plan, before, all) };
}

/** The enabled containers before the draft in its campaign group (a new one goes last), from the last one that
 *  does not start "after the one before" — nothing earlier reaches past it, and a switched-off one chains nothing. */
export function chainBefore(plan: DropGenPlanDetail, containerId: string | null, masterId: string | null): TimedContainerInput[] {
  const at = containerId ? plan.containers.findIndex((c) => c.id === containerId) : -1;
  const group = (at < 0 ? plan.containers : plan.containers.slice(0, at)).filter((c) => c.enabled && (c.masterId ?? '') === (masterId ?? ''));
  let root = group.length - 1;
  while (root > 0 && group[root].startMode === 'AFTER_PREVIOUS') root -= 1;
  return group.slice(Math.max(0, root));
}

/** The container "after the one before" follows: the last of the chain (enabled, same campaign group). */
function previousOf(plan: DropGenPlanDetail, before: TimedContainerInput[], all: Map<string, ContainerTimes>): PreviousContainer | null {
  const prev = before[before.length - 1];
  const row = prev ? plan.containers.find((c) => c.id === prev.id) : undefined;
  const t = prev ? all.get(prev.id) : undefined;
  return row && t ? { name: row.name, color: row.color, lastMin: t.lastPlannedMin } : null;
}
